import { useMemo } from 'react';
import { useRoles } from './useRoles';
import type { Role } from '../types/database';
import type { GroupRoleTone } from '../types/groups.types';
import {
  getRoleTone,
  setRoleRegistry,
  sortRolesForDisplay,
} from '../utils/roles.utils';
import { formatUserRole } from '../utils/users.utils';

export type RoleOption = {
  value: string;
  label: string;
};

/**
 * Single source of truth for rendering roles across the app. Sources the role
 * list from the database (useRoles) and derives the ordered list, select
 * options, and per-role labels/tones that components used to read from
 * hardcoded constants. New roles created by admins flow through automatically.
 */
export function useRoleDirectory() {
  const query = useRoles();
  const roles = useMemo(
    () => sortRolesForDisplay(query.data ?? []),
    [query.data]
  );
  if (query.data) {
    setRoleRegistry(roles);
  }

  const byKey = useMemo(
    () => new Map<string, Role>(roles.map((role) => [role.key, role])),
    [roles]
  );

  const orderedKeys = useMemo(() => roles.map((role) => role.key), [roles]);

  const options = useMemo<RoleOption[]>(
    () => roles.map((role) => ({ value: role.key, label: role.display_name })),
    [roles]
  );

  const getLabel = (key: string | null | undefined): string => {
    if (!key) {
      return formatUserRole(key);
    }
    return byKey.get(key)?.display_name ?? formatUserRole(key);
  };

  const getTone = (key: string | null | undefined): GroupRoleTone =>
    getRoleTone(key ? byKey.get(key)?.chip_color : null);

  return {
    roles,
    byKey,
    orderedKeys,
    options,
    getLabel,
    getTone,
    isLoading: query.isLoading,
    error: query.error,
  };
}
